import { useState, useEffect, useCallback } from "react";
import { TrendingUp, Info, RotateCcw, HelpCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useToast } from "../../components/ui/toast";
import { setCompleted } from "../../lib/uwProgress";
import { getTotalRooms } from "../../lib/rooms";
import { formatCurrency } from "../../lib/utils";
import { Deal } from "../../types/deal";
import { OpexState } from "../../types/opex";
import {
  calculateOpexResults,
  calculateYear3Amount,
  getDriverLabel,
  RevenueData,
} from "../../lib/opexCalc";
import { createDefaultOpexState } from "../../data/opexDefaults";
import BenchmarkMeter from "../../components/BenchmarkMeter";
import { computeAdvancedAnnual } from "../../lib/fnbCalc";
import { calculateOtherRevenue } from "../../lib/otherRevenueCalc";
import { getDataSource } from "../../lib/datasource";
import { env } from "../../lib/env";
import { patchProjectKpis } from "../../lib/datasource";
import type { Kpis } from "../../lib/kpis/defaults";
import { debounce } from "@/utils/useDebounce";
import { createOpexStateFromTemplateKpis } from "./schemas/helpersExpenses";
import { getPayrollDataById } from "@/hooks/payrollDataService";

type TemplateModeProps = {
  kpis?: Kpis | null;
  onChange?: (next: Kpis) => void;
  hideActions?: boolean;
  deal?: Deal | null;
};

type ProjectModeProps = {
  dealId: string;
  onSaved?: () => void;
};

type Props = ProjectModeProps & TemplateModeProps;

interface OperatingExpensesProps extends Props {}

interface PayrollTotals {
  totalFTEs: string | number;
  annualCost: number;
  totalMonthly: number;
  socialCharges: number;
  monthlyPayroll: number;
  socialChargesPercent?: number;
}

export default function OperatingExpenses(props: OperatingExpensesProps) {
  const { dealId, onSaved, hideActions, deal: dealProps } = props;

  const { toast } = useToast();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [opexState, setOpexState] = useState<OpexState | null>(null);
  const [originalState, setOriginalState] = useState<OpexState | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "success">(
    "idle",
  );
  const [animatedFields, setAnimatedFields] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const [payrollTotals, setPayrollTotals] = useState<PayrollTotals | null>(
    null,
  );
  const [payrollLoading, setPayrollLoading] = useState(false);

  useEffect(() => {
    async function loadOpexData() {
      setLoading(true);

      try {
        let opexData: OpexState;
        let loadedFromTemplate = false;

        const ds = getDataSource();
        const response = await ds.getProject(dealId);

        let project = null;

        if (Array.isArray(response) && response.length > 0) {
          project = response[0];
        } else if (
          typeof response === "object" &&
          response !== null &&
          !Array.isArray(response)
        ) {
          project = response;
        }

        if (!project) {
          toast.error("Project data not found.");
          setLoading(false);
          return;
        }

        setDeal(project as Deal);

        const projectKpis = project?.kpis;

        if (projectKpis?.opex) {
          if (Array.isArray(projectKpis.opex?.items)) {
            opexData = projectKpis.opex as OpexState;
            loadedFromTemplate = true;
          } else if (
            typeof projectKpis.opex === "object" &&
            projectKpis.opex !== null &&
            !Array.isArray(projectKpis.opex)
          ) {
            opexData = createOpexStateFromTemplateKpis(projectKpis);
            loadedFromTemplate = true;
          }
        }

        if (!loadedFromTemplate) {
          opexData = createDefaultOpexState();
        }

        setOpexState(opexData);
        setOriginalState(opexData);

        const payrollDataId = projectKpis?.payroll?.dataId;
        if (payrollDataId) {
          setPayrollLoading(true);
          try {
            const payrollRecord = await getPayrollDataById(payrollDataId);
            if (payrollRecord?.data?.totals) {
              setPayrollTotals({
                ...(payrollRecord.data.totals as PayrollTotals),
                socialChargesPercent: payrollRecord.data.socialChargesPercent,
              });
            }
          } catch (e) {
            console.warn("[OperatingExpenses] Could not load payroll data:", e);
          } finally {
            setPayrollLoading(false);
          }
        }
      } catch (error) {
        console.error("[OperatingExpenses] Error loading opex data:", error);
        toast.error("Failed to load operating expenses");
        const defaultState = createDefaultOpexState();
        setOpexState(defaultState);
        setOriginalState(defaultState);
      } finally {
        setLoading(false);
      }
    }

    if (dealId) {
      loadOpexData();
    }
  }, [dealId]);

  const animateFields = useCallback((fields: string[]) => {
    setAnimatedFields(new Set(fields));
    setTimeout(() => setAnimatedFields(new Set()), 1000);
  }, []);

  const debouncedRecalculate = useCallback(
    debounce(() => {
      animateFields(["kpis", "totals"]);
    }, 200),
    [animateFields],
  );

  const getRevenueData = (): RevenueData => {
    const roomsRevenue = deal?.roomRevenue?.totals.roomsRevenue || 0;
    const fnbRevenue = deal?.fnbRevenue
      ? (() => {
          if (deal.roomRevenue) {
            const roomsSelectors = {
              roomsAvailableByMonth: deal.roomRevenue.months.map(
                (m) => m.roomsAvailable,
              ),
              roomsSoldByMonth: deal.roomRevenue.months.map((m) => m.roomsSold),
              roomsAvailableYearTotal: deal.roomRevenue.totals.roomsAvailable,
              roomsSoldYearTotal: deal.roomRevenue.totals.roomsSold,
            };
            try {
              const results = computeAdvancedAnnual(
                deal.fnbRevenue,
                roomsSelectors,
              );
              return results.totalFnb;
            } catch {
              return 0;
            }
          }
          return 0;
        })()
      : 0;

    const otherRevenue = deal?.otherRevenue
      ? (() => {
          try {
            const roomsData = {
              totalRoomsRevenue: roomsRevenue,
              roomsAvailableYearTotal:
                deal?.roomRevenue?.totals.roomsAvailable || 0,
            };
            const results = calculateOtherRevenue(deal.otherRevenue, roomsData);
            return results.totalAncillary;
          } catch {
            return 0;
          }
        })()
      : 0;

    const totalRevenue = roomsRevenue + fnbRevenue + otherRevenue;
    const roomsSoldByMonth =
      deal?.roomRevenue?.months.map((m) => m.roomsSold) || Array(12).fill(0);
    const roomsSoldYearTotal = deal?.roomRevenue?.totals.roomsSold || 0;

    return {
      totalRoomsRevenue: roomsRevenue,
      totalFnbRevenue: fnbRevenue,
      totalOtherRevenue: otherRevenue,
      totalRevenue,
      roomsSoldByMonth,
      roomsSoldYearTotal,
    };
  };

  const handleItemChange = (itemId: string, value: number) => {
    if (!opexState) return;

    const newState = {
      ...opexState,
      items: opexState.items.map((item) =>
        item.id === itemId ? { ...item, value: Math.max(0, value) } : item,
      ),
    };

    setOpexState(newState);
    debouncedRecalculate();
  };

  const handleRestoreDefaults = () => {
    const defaultState = createDefaultOpexState();
    setOpexState(defaultState);
    animateFields(["all-items", "kpis", "totals"]);
    toast.success("Restored to default values");
  };

  const handleSave = async () => {
    if (!deal || !opexState) return;

    setSaveState("saving");

    try {
      if (env.DATA_SOURCE === "supabase" && dealId) {
        const ds = getDataSource();
        const response = await ds.getProject(dealId);
        let project = null;

        if (Array.isArray(response) && response.length > 0) {
          project = response[0];
        } else if (
          typeof response === "object" &&
          response !== null &&
          !Array.isArray(response)
        ) {
          project = response;
        }

        const updatedOpex: any = {};

        opexState.items.forEach((item) => {
          switch (item.id) {
            case "roomsCommission":
              updatedOpex.roomsCommission = item.value;
              break;
            case "guestSupplies":
              updatedOpex.guestSuppliesCleaning = item.value;
              break;
            case "cogs":
              updatedOpex.cogs = item.value;
              break;
            case "meetingEvents":
              updatedOpex.meetingsEvents = item.value;
              break;
            case "wellnessOther":
              updatedOpex.wellnessOther = item.value;
              break;
            case "otherDirect":
              updatedOpex.otherDirect = item.value;
              break;
            case "adminGeneral":
              updatedOpex.adminGeneralOther = item.value;
              break;
            case "techSubs":
              updatedOpex.techSubscriptions = item.value;
              break;
            case "salesMarketing":
              updatedOpex.salesMarketingOther = item.value;
              break;
            case "maintenance":
              updatedOpex.maintenanceOther = item.value;
              break;
            case "utilities":
              updatedOpex.utilities = item.value;
              break;
            case "mgmtFee":
              updatedOpex.mgmtFee = item.value;
              break;
            case "propertyTaxes":
              updatedOpex.propertyTaxes = item.value;
              break;
            case "insurance":
              updatedOpex.insurance = item.value;
              break;
            case "rent":
              updatedOpex.rent = item.value;
              break;
          }
        });

        const updatedKpis = {
          ...(project?.kpis || {}),
          opex: {
            ...(project?.kpis?.opex || {}),
            ...updatedOpex,
          },
        };

        await patchProjectKpis(dealId, { opex: updatedKpis.opex });
      }

      setCompleted(dealId, "operatingCosts", true);
      setOriginalState(opexState);
      setSaveState("success");
      toast.success("Operating expenses saved");

      if (onSaved) onSaved();

      setTimeout(() => setSaveState("idle"), 2000);
    } catch (error) {
      console.error("[OperatingExpenses] Save error:", error);
      setSaveState("idle");
      toast.error("Failed to save operating expenses");
    }
  };

  const handleCancel = () => {
    if (originalState) {
      setOpexState(originalState);
      toast.info("Changes discarded");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading operating expenses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!deal || !opexState) {
    return <div>Loading...</div>;
  }

  const rooms = getTotalRooms(deal);

  if (rooms === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Operating Expenses
          </h3>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800 font-medium">
                Complete Room Revenue setup to model operating expenses.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const revenueData = getRevenueData();
  const results = calculateOpexResults(opexState, revenueData);

  const getAnimationClass = (field: string) => {
    return animatedFields.has(field) || animatedFields.has("all-items")
      ? "animate-pulse bg-brand-50 transition-all duration-500"
      : "";
  };

  const getSectionColor = (section: string) => {
    switch (section) {
      case "DIRECT":
        return "bg-green-50 border-green-200";
      case "INDIRECT":
        return "bg-blue-50 border-blue-200";
      case "OTHER":
        return "bg-purple-50 border-purple-200";
      default:
        return "bg-slate-50 border-slate-200";
    }
  };

  const getSectionItems = (section: string) => {
    return opexState.items.filter((item) => item.section === section);
  };

  const renderSection = (
    section: "DIRECT" | "INDIRECT" | "OTHER",
    title: string,
  ) => {
    const items = getSectionItems(section);
    const sectionTotal = results.bySection[section];

    return (
      <Card key={section} className="border-slate-200 shadow-md">
        <CardHeader
          className={`${getSectionColor(section)} rounded-t-lg px-6 py-4`}
        >
          <CardTitle className="text-lg font-bold flex items-center justify-between">
            <span>{title}</span>
            <span className="text-base font-extrabold">
              {formatCurrency(sectionTotal)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:px-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead className="bg-white">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-500 text-sm border-b border-slate-200 min-w-[200px]">
                    Cost Item
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-500 text-sm border-b border-slate-200 min-w-[140px]">
                    Value
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-500 text-sm border-b border-slate-200 min-w-[200px]">
                    Calculation Method
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-500 text-sm border-b border-slate-200 min-w-[150px]">
                    Annual Total
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-500 text-sm border-b border-slate-200 min-w-[280px]">
                    Benchmark
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const isActive = index === items.length - 1;
                  const year3Amount = calculateYear3Amount(
                    item,
                    deal,
                    revenueData,
                  );

                  return (
                    <tr
                      key={item.id}
                      className={`border-b border-slate-100 transition-colors ${
                        isActive ? "border-emerald-200" : "hover:bg-slate-50"
                      } ${getAnimationClass(item.id)}`}
                    >
                      <td className="py-5 px-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-slate-900">
                            {item.label}
                          </span>
                          {item.tooltip && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-slate-400 hover:text-brand-600 transition-colors"
                                  >
                                    <HelpCircle className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs bg-slate-800 text-white">
                                  <p className="text-sm">{item.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-4 text-right">
                        <div className="relative inline-block">
                          <span className="absolute left-2 top-2.5 text-slate-500 text-sm">
                            {item.unit}
                          </span>
                          <input
                            type="number"
                            value={item.value || ""}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                Number(e.target.value) || 0,
                              )
                            }
                            className={`w-24 px-3 py-2 border border-slate-300 rounded-lg shadow-inner text-right text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors ${
                              item.unit === "$" ? "pl-7" : ""
                            }`}
                            min="0"
                            step={item.unit === "$" ? "1" : "0.1"}
                          />
                        </div>
                      </td>
                      <td className="py-5 px-4">
                        <div className="relative">
                          <select
                            disabled
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-600 cursor-not-allowed shadow-sm"
                          >
                            <option>{getDriverLabel(item.driver)}</option>
                          </select>
                        </div>
                      </td>
                      <td className="py-5 px-4 text-right">
                        <span
                          className={`text-base font-bold text-slate-900 ${isActive ? "text-emerald-700" : ""}`}
                        >
                          {formatCurrency(year3Amount, deal.currency)}
                        </span>
                      </td>
                      <td className="py-5 px-4">
                        {item.benchmark && (
                          <BenchmarkMeter
                            label={item.label}
                            currentValue={item.value}
                            target={item.benchmark.target}
                            min={item.benchmark.min}
                            max={item.benchmark.max}
                            unit={item.benchmark.unit}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  const currency = deal.currency || "EUR";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Operating Expenses
          </h3>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-blue-800 text-sm font-medium">
              USALI-compliant operating cost structure with departmental and
              undistributed expenses.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRestoreDefaults}
          className="flex items-center space-x-2"
        >
          <RotateCcw className="h-4 w-4" />
          <span>Restore Defaults</span>
        </Button>
      </div>

      {/* KPI Cards */}
      <div
        className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${getAnimationClass("kpis")}`}
      >
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Direct Costs</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(results.directTotal, currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">
            Indirect Costs
          </div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(results.indirectTotal, currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Other Costs</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(results.otherTotal, currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">
            Total Operating Expenses
          </div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(results.grandTotal, currency)}
          </div>
        </div>
      </div>

      {/* Cost Sections */}
      <div className="space-y-6">
        {renderSection("DIRECT", "Direct Costs")}
        {renderSection("INDIRECT", "Indirect Costs")}
        {renderSection("OTHER", "Other Costs")}
      </div>

      {/* Payroll Block (Read-only) */}
      <Card className="border-slate-200 bg-slate-50/50">
        <CardHeader>
          <CardTitle className="text-base text-slate-700">
            Payroll Costs (from Payroll tab)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {payrollLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full mr-2" />
                <span className="text-sm text-slate-500">
                  Loading payroll data...
                </span>
              </div>
            ) : payrollTotals ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monthly Payroll</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(payrollTotals.monthlyPayroll, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Social Charges</span>
                    <span className="font-medium text-slate-900">
                      ({payrollTotals.socialChargesPercent}%)
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Monthly</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(payrollTotals.totalMonthly, currency)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total FTEs</span>
                    <span className="font-medium text-slate-900">
                      {Number(payrollTotals.totalFTEs).toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                    <span className="text-slate-700 font-medium">
                      Total Annual Payroll
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(payrollTotals.annualCost, currency)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-500 text-sm py-2">
                Configure payroll in the Payroll tab to see totals here
              </div>
            )}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs text-slate-500 italic">
                Payroll costs are calculated in the Payroll tab and included
                automatically. They can't be edited here.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {!hideActions && (
        <div className="flex items-center space-x-3 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saveState === "saving"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveState === "saving"}
            className={`${
              saveState === "success"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"
            } text-white flex items-center space-x-2`}
          >
            {saveState === "saving" && (
              <TrendingUp className="h-4 w-4 animate-spin" />
            )}
            {saveState === "success" && <TrendingUp className="h-4 w-4" />}
            <span>
              {saveState === "saving" && "Saving..."}
              {saveState === "success" && "Saved ✓"}
              {saveState === "idle" && "Save & Update KPIs"}
            </span>
          </Button>
        </div>
      )}

      <div className="text-center">
        <p className="text-xs text-slate-500">
          Recalculated • {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
